import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Computes the collapsed max-height for the grid wrapper.
 * At viewport heights >= 800px: 20% of the full height.
 * Below 800px: linearly interpolates down to the heading height at 400px and below.
 * @param {HTMLElement} cp - The cart-recommendations element.
 * @param {number} fullH - The full scrollHeight of the grid wrapper.
 * @returns {number}
 */
function cpCollapseHeight(cp, fullH) {
  const heading = /** @type {HTMLElement | null} */ (cp.querySelector('.cart-recommendations__heading'));
  const titleH = heading ? heading.offsetHeight : 40;
  const h = window.innerHeight;
  if (h >= 800) return Math.round(fullH * 0.2);
  const t = Math.max(0, (h - 400) / (800 - 400));
  return Math.round(titleH + t * (Math.round(fullH * 0.2) - titleH));
}

/**
 * Renders the collapsed state, with a height derived from the current viewport height.
 * @param {HTMLElement} cp - The cart-recommendations element.
 * @param {boolean} animate - Whether to animate the transition.
 */
function cpCollapse(cp, animate) {
  const wrapper = /** @type {HTMLElement | null} */ (cp.querySelector('.cart-recommendations__grid-wrapper'));
  if (!wrapper) return;

  const fullH = wrapper.scrollHeight;
  if (!fullH) return;

  cp.dataset.cpFullHeight = String(fullH);
  if (animate) {
    wrapper.style.maxHeight = fullH + 'px';
    wrapper.offsetHeight; // force reflow so transition fires
  } else {
    wrapper.style.transition = 'none';
  }
  wrapper.style.maxHeight = cpCollapseHeight(cp, fullH) + 'px';
  cp.classList.add('cart-recommendations--collapsed');
  if (!animate) requestAnimationFrame(() => { wrapper.style.transition = ''; });
}

/**
 * Renders the expanded state.
 * @param {HTMLElement} cp - The cart-recommendations element.
 * @param {boolean} animate - Whether to animate the transition.
 */
function cpExpand(cp, animate) {
  const wrapper = /** @type {HTMLElement | null} */ (cp.querySelector('.cart-recommendations__grid-wrapper'));
  if (!wrapper) return;

  const fullH = wrapper.scrollHeight;
  if (!fullH) return;

  cp.dataset.cpFullHeight = String(fullH);
  if (animate) {
    wrapper.style.maxHeight = wrapper.scrollHeight + 'px';
    wrapper.offsetHeight; // force reflow so transition fires
    wrapper.style.transition = '';
  } else {
    wrapper.style.transition = 'none';
    wrapper.style.maxHeight = fullH + 'px';
    requestAnimationFrame(() => { wrapper.style.transition = ''; });
  }
  cp.classList.remove('cart-recommendations--collapsed');
}

class CartRecommendations extends Component {

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** True until the first successful recommendations load completes. */
  #isInitialLoad = true;

  connectedCallback() {
    super.connectedCallback();
    this.#loadRecommendations();
    this.#mutationObserver.observe(this, { attributes: true });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartFinished);
    document.addEventListener(ThemeEvents.cartOpenedEvent, this.#handleCartOpened);
    this.addEventListener('click', this.#handleToggleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mutationObserver.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartFinished);
    document.removeEventListener(ThemeEvents.cartOpenedEvent, this.#handleCartOpened);
    this.removeEventListener('click', this.#handleToggleClick);
  }

  #onCartFinished = () => {
    this.dataset.recommendationsPerformed = 'false';
    this.#loadRecommendations();
  };

  /**
   * Fetches the users preferred collapsed/expanded state from session storage and applies it.
   * Below 800px viewport height the default is collapsed, unless the user has explicitly expanded.
   */
  #restoreState = () => {
    if (window.innerHeight >= 1000) return;
    if (window.innerHeight < 800) {
      cpCollapse(this, false);
      return;
    }
    const shouldCollapse = sessionStorage.getItem('cp-collapsed');
    if (shouldCollapse === '1') {
      cpCollapse(this, false);
    }
  };

  /**
   * Restores state each time the cart drawer is opened.
   * Deferred by one rAF because CartOpenedEvent fires before showDialog()'s own
   * rAF has called dialog.showModal() — without the defer, scrollHeight is 0.
   */
  #handleCartOpened = () => {
    requestAnimationFrame(() => this.#restoreState());
  };

  /**
   * Click handler for the collapsed/expanded toggle button.
   * @param {MouseEvent} e
   */
  #handleToggleClick = (e) => {
    const btn = /** @type {Element} */ (e.target)?.closest('.cart-recommendations__toggle');
    if (!btn) return;

    const wrapper = this.querySelector('.cart-recommendations__grid-wrapper');
    if (!wrapper) return;

    if (!this.classList.contains('cart-recommendations--collapsed')) {
      cpCollapse(this, true);
      sessionStorage.setItem('cp-collapsed', '1');
    } else {
      cpExpand(this, true);
      sessionStorage.setItem('cp-collapsed', '0');
    }
  };

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`cart-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          if (this.#isInitialLoad) {
            this.#isInitialLoad = false;
            this.#restoreState();
          }
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Cart recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading cart recommendations';
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
